import React, { useCallback, useId, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FiCheck, FiCopy, FiKey } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { copyTextToClipboard } from '../../blockchain/offlineSigning';
import { walletApi } from '../../api/smartTokenizerApi';

/** CIP-100 (ICAN): https://cip.coreblockchain.net/cip/cbc/cip-100/ — machine form is 44 contiguous hex chars (CID + checksum + BCAN). */
export const CIP100_ICAN_DOC_URL = 'https://cip.coreblockchain.net/cip/cbc/cip-100/';

/**
 * CIP-100 human representation: all letters uppercase, groups of four characters separated by spaces.
 * @param {string} machineHex — 44 hex characters (no 0x), or null if invalid
 * @returns {string | null}
 */
export function formatIcanHumanRepresentationCip100(machineHex) {
  const raw = String(machineHex ?? '')
    .trim()
    .replace(/^0x/i, '');
  if (!/^[a-fA-F0-9]{44}$/.test(raw)) {
    return null;
  }
  const upper = raw.toUpperCase();
  const parts = [];
  for (let i = 0; i < upper.length; i += 4) {
    parts.push(upper.slice(i, i + 4));
  }
  return parts.join(' ');
}

/** CIP-100 network table (CID ↔ chain id). */
export function describeCip100CidForChainId(chainId) {
  const id = String(chainId ?? '').trim();
  switch (id) {
    case '1':
      return { cid: 'cb', label: 'mainnet' };
    case '3':
    case '4':
      return { cid: 'ab', label: 'testnet' };
    default:
      return id ? { cid: null, label: 'enterprise / other (see CIP-100)' } : { cid: null, label: null };
  }
}

export const WalletInputRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0.5rem;

  input {
    flex: 1;
    min-width: 0;
  }
`;

export const GenerateWalletBtn = styled.button.attrs({ type: 'button' })`
  flex-shrink: 0;
  padding: 0 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(79, 172, 254, 0.45);
  background: rgba(79, 172, 254, 0.12);
  color: #7dd3fc;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(79, 172, 254, 0.22);
    border-color: #4facfe;
    color: #fff;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const KeyRevealBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 12000;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const KeyRevealCard = styled.div`
  background: linear-gradient(135deg, #1e1b3d 0%, #151028 100%);
  border: 1px solid rgba(248, 113, 113, 0.35);
  border-radius: 14px;
  max-width: 600px;
  max-height: min(90vh, 720px);
  overflow-y: auto;
  width: 100%;
  padding: 1.35rem 1.5rem;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.55);
`;

const KeyRevealTitle = styled.h3`
  margin: 0 0 0.5rem;
  color: #fecaca;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const KeyRevealWarning = styled.p`
  margin: 0 0 1rem;
  color: #fca5a5;
  font-size: 0.88rem;
  line-height: 1.55;
  font-weight: 600;
`;

const Cip100Hint = styled.div`
  margin: 0 0 1rem;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(125, 211, 252, 0.25);
  background: rgba(79, 172, 254, 0.08);
  color: #cbd5e1;
  font-size: 0.8rem;
  line-height: 1.5;

  strong {
    color: #e2e8f0;
  }

  code {
    font-family: Consolas, Monaco, monospace;
    font-size: 0.76rem;
    color: #7dd3fc;
  }

  a {
    color: #7dd3fc;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  a:hover {
    color: #bae6fd;
  }
`;

const HumanIcanLine = styled.div`
  margin-top: 0.5rem;
  font-size: 0.72rem;
  color: #94a3b8;
  line-height: 1.45;

  span.mono {
    font-family: Consolas, Monaco, monospace;
    font-size: 0.74rem;
    color: #e2e8f0;
    word-break: break-all;
  }
`;

const SecretBox = styled.div`
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.65rem 0.75rem;
  margin-bottom: 0.75rem;
  font-family: Consolas, Monaco, monospace;
  font-size: 0.78rem;
  word-break: break-all;
  color: #e2e8f0;
`;

const SecretLabel = styled.div`
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 0.35rem;
  font-weight: 600;
`;

const SecretRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;

  button {
    flex-shrink: 0;
    padding: 0.35rem 0.5rem;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(255, 255, 255, 0.06);
    color: #e2e8f0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.72rem;

    &:hover {
      background: rgba(255, 255, 255, 0.12);
    }
  }
`;

const AckRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin: 1rem 0;
  cursor: pointer;
  color: #cbd5e1;
  font-size: 0.85rem;
  line-height: 1.45;

  input {
    margin-top: 0.2rem;
    flex-shrink: 0;
    accent-color: #4facfe;
  }
`;

const KeyRevealActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.65rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
`;

const KeyRevealButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  &.primary {
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    color: #fff;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(79, 172, 254, 0.3);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

/**
 * Requests ephemeral Core (Xcb.Net) wallet material from the API; one-time display of secrets.
 * @param {object} opts
 * @param {string} opts.walletAddress current draft address
 * @param {(address: string) => void} opts.setWalletAddress
 */
export function useOneTimeWalletReveal({ walletAddress, setWalletAddress }) {
  const [reveal, setReveal] = useState(null);
  const [revealAcknowledged, setRevealAcknowledged] = useState(false);
  const [generating, setGenerating] = useState(false);

  const walletFieldEmpty = !String(walletAddress || '').trim();

  const generateWallet = useCallback(async () => {
    if (!walletFieldEmpty) {
      toast.error('Clear the wallet field first to generate a new account.');
      return;
    }
    setGenerating(true);
    try {
      const { data } = await walletApi.generateCoreOfflineMaterial({});
      setWalletAddress(data.ican);
      setRevealAcknowledged(false);
      setReveal({
        address: data.ican,
        mnemonic: data.mnemonic,
        extendedPrivateKeyHex: data.extendedPrivateKeyHex,
        derivationPath: data.derivationPath,
        chainId: data.chainId
      });
    } catch (e) {
      const status = e.response?.status;
      const msg =
        status === 401
          ? 'Sign in to generate Core wallet material.'
          : e.response?.data?.message ||
            e.response?.data?.Message ||
            e.message ||
            'Could not generate wallet material';
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  }, [walletFieldEmpty, setWalletAddress]);

  const dismissKeyReveal = useCallback(() => {
    setReveal(null);
    setRevealAcknowledged(false);
  }, []);

  const cancelKeyGeneration = useCallback(() => {
    setReveal(null);
    setRevealAcknowledged(false);
    setWalletAddress('');
    toast('Generation cancelled. Wallet field cleared — generate again or paste an address if you still need one.');
  }, [setWalletAddress]);

  return {
    reveal,
    revealAcknowledged,
    setRevealAcknowledged,
    generateWallet,
    generating,
    dismissKeyReveal,
    cancelKeyGeneration,
    walletFieldEmpty,
    keyRevealOpen: Boolean(reveal)
  };
}

/**
 * @param {object} props
 * @param {{
 *   address: string,
 *   mnemonic: string,
 *   extendedPrivateKeyHex: string,
 *   derivationPath: string,
 *   chainId: string
 * } | null} props.reveal
 * @param {boolean} props.revealAcknowledged
 * @param {(v: boolean) => void} props.onRevealAcknowledgedChange
 * @param {() => void} props.onConfirmBackup
 * @param {() => void} props.onCancelDiscard
 * @param {string} [props.publicAddressCaption]
 */
export function OneTimeKeyRevealDialog({
  reveal,
  revealAcknowledged,
  onRevealAcknowledgedChange,
  onConfirmBackup,
  onCancelDiscard,
  publicAddressCaption = 'ICAN — machine form (44 hex, CIP-100; also filled in the form)'
}) {
  const titleId = useId();

  const humanIcan = useMemo(() => formatIcanHumanRepresentationCip100(reveal?.address), [reveal?.address]);
  const cipNetwork = useMemo(() => describeCip100CidForChainId(reveal?.chainId), [reveal?.chainId]);

  if (!reveal) return null;

  return (
    <KeyRevealBackdrop
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={() =>
        toast.error(
          'Use the buttons below. Confirm you saved the mnemonic and extended private key before continuing.'
        )
      }
    >
      <KeyRevealCard onClick={(e) => e.stopPropagation()}>
        <KeyRevealTitle id={titleId}>
          <FiKey aria-hidden />
          Save your Core wallet material now
        </KeyRevealTitle>
        <KeyRevealWarning>
          These values are shown <strong>only this once</strong>. The API generates them in memory and does not persist
          them.{' '}
          <strong>
            To broadcast deploys and contract calls to a Core (go-core) node, sign prepared JSON offline with
            Ping.Tokenizer.Signer using the <strong>24-word mnemonic</strong> or <strong>228 hex digits</strong> (114-byte
            Xcb.Net extended private key) below — not an Ethereum secp256k1 key; go-core expects the native transaction
            format for{' '}
            <code>xcb_sendRawTransaction</code>.
          </strong>
        </KeyRevealWarning>
        <Cip100Hint>
          <strong>ICAN (CIP-100)</strong> — International Crypto Account Number:{' '}
          <strong>2 hex (CID)</strong> + <strong>2 hex (checksum, mod 97)</strong> +{' '}
          <strong>40 hex (BCAN, 20-byte account)</strong> = 44 hex characters machine form.{' '}
          <a href={CIP100_ICAN_DOC_URL} target="_blank" rel="noopener noreferrer">
            CIP-100 specification
          </a>
          .
          {cipNetwork.cid && reveal.chainId ? (
            <>
              {' '}
              For chain ID <code>{reveal.chainId}</code>, the network prefix is typically <strong>{cipNetwork.cid}</strong>{' '}
              ({cipNetwork.label}).
            </>
          ) : null}
        </Cip100Hint>
        <SecretLabel>{publicAddressCaption}</SecretLabel>
        <SecretRow>
          <SecretBox style={{ marginBottom: 0, flex: 1 }}>{reveal.address}</SecretBox>
          <button
            type="button"
            onClick={async () => {
              await copyTextToClipboard(reveal.address);
              toast.success('ICAN (machine form) copied');
            }}
          >
            <FiCopy /> Copy
          </button>
        </SecretRow>
        {humanIcan ? (
          <>
            <HumanIcanLine>
              Human-readable (CIP-100: uppercase, groups of four){' '}
              <button
                type="button"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  marginLeft: 6,
                  padding: '0.2rem 0.45rem',
                  fontSize: '0.68rem',
                  borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#e2e8f0',
                  cursor: 'pointer'
                }}
                onClick={async () => {
                  await copyTextToClipboard(humanIcan);
                  toast.success('Human-readable ICAN copied');
                }}
              >
                <FiCopy size={12} /> Copy
              </button>
            </HumanIcanLine>
            <SecretBox style={{ marginTop: '0.35rem', marginBottom: 0, fontSize: '0.76rem' }}>{humanIcan}</SecretBox>
          </>
        ) : null}
        <SecretLabel style={{ marginTop: '0.75rem' }}>24-word mnemonic (never share or commit)</SecretLabel>
        <SecretRow>
          <SecretBox style={{ marginBottom: 0, flex: 1, whiteSpace: 'pre-wrap' }}>{reveal.mnemonic}</SecretBox>
          <button
            type="button"
            onClick={async () => {
              await copyTextToClipboard(reveal.mnemonic);
              toast.success('Mnemonic copied');
            }}
          >
            <FiCopy /> Copy
          </button>
        </SecretRow>
        <SecretLabel style={{ marginTop: '0.75rem' }}>
          Extended private key — Xcb.Net (228 hex digits, never share or commit)
        </SecretLabel>
        <SecretRow>
          <SecretBox style={{ marginBottom: 0, flex: 1 }}>{reveal.extendedPrivateKeyHex}</SecretBox>
          <button
            type="button"
            onClick={async () => {
              await copyTextToClipboard(reveal.extendedPrivateKeyHex);
              toast.success('Extended key copied');
            }}
          >
            <FiCopy /> Copy
          </button>
        </SecretRow>
        <SecretLabel style={{ marginTop: '0.75rem' }}>Derivation path (Ping.Tokenizer.Signer)</SecretLabel>
        <SecretRow>
          <SecretBox style={{ marginBottom: 0, flex: 1 }}>{reveal.derivationPath}</SecretBox>
          <button
            type="button"
            onClick={async () => {
              await copyTextToClipboard(reveal.derivationPath);
              toast.success('Path copied');
            }}
          >
            <FiCopy /> Copy
          </button>
        </SecretRow>
        <SecretLabel style={{ marginTop: '0.75rem' }}>Chain ID</SecretLabel>
        <SecretBox style={{ marginBottom: 0 }}>{reveal.chainId}</SecretBox>
        <AckRow>
          <input
            type="checkbox"
            checked={revealAcknowledged}
            onChange={(e) => onRevealAcknowledgedChange(e.target.checked)}
          />
          <span>
            I have stored the mnemonic and extended private key in a secure place. I understand they will not be shown
            again.
          </span>
        </AckRow>
        <KeyRevealActions>
          <KeyRevealButton type="button" className="secondary" onClick={onCancelDiscard}>
            Cancel — discard this wallet
          </KeyRevealButton>
          <KeyRevealButton
            type="button"
            className="primary"
            disabled={!revealAcknowledged}
            onClick={onConfirmBackup}
          >
            <FiCheck />
            Continue — I have backed up my Core wallet material
          </KeyRevealButton>
        </KeyRevealActions>
      </KeyRevealCard>
    </KeyRevealBackdrop>
  );
}
